import { useNavigate, useSearch } from "@tanstack/react-router"
import React, { useEffect, useState } from "react"

import Analytics from "../components/ContentManagement/Analytics"
import BrandManagement from "../components/ContentManagement/BrandManagement"
import ContentApproval from "../components/ContentManagement/ContentApproval"
import ContentCreation from "../components/ContentManagement/ContentCreation"
import CreatorManagement from "../components/ContentManagement/CreatorManagement"
import IdeaManagement from "../components/ContentManagement/IdeaManagement"
import ScheduledPosts from "../components/ContentManagement/ScheduledPosts"
import TemplateManagement from "../components/ContentManagement/TemplateManagement"
import { trpc } from "../lib/trpc"

// Sub-component imports (will create these next)
type TabType =
  | "dashboard"
  | "brands"
  | "creators"
  | "approval"
  | "ideas"
  | "templates"
  | "content"
  | "scheduled"
  | "analytics"

const ContentManagementScreen: React.FC = () => {
  const navigate = useNavigate()
  const searchParams = useSearch({ from: "/content-management" })

  const [activeTab, setActiveTab] = useState<TabType>("dashboard")
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null)

  // Initialize state from URL params
  useEffect(() => {
    if (
      searchParams.tab &&
      [
        "dashboard",
        "brands",
        "creators",
        "approval",
        "ideas",
        "templates",
        "content",
        "scheduled",
        "analytics",
      ].includes(searchParams.tab)
    ) {
      setActiveTab(searchParams.tab as TabType)
    }
    if (searchParams.brand) {
      setSelectedBrand(searchParams.brand)
    }
  }, [searchParams])

  // Update URL when tab changes
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab)
    navigate({
      to: "/content-management",
      search: { ...searchParams, tab },
    })
  }

  // Update URL when brand changes
  const handleBrandSelect = (brandId: string | null) => {
    setSelectedBrand(brandId)
    navigate({
      to: "/content-management",
      search: { ...searchParams, brand: brandId || undefined },
    })
  }

  // Fetch dashboard stats
  const { data: brands, isLoading: brandsLoading } = trpc.contentManagement.listBrands.useQuery()
  // const { data: contentStats } = trpc.contentManagement.getContentStats.useQuery({
  //   brandId: selectedBrand || undefined,
  // });
  console.log("brands", brands)
  const contentStats = {
    totalContent: 0,
    draftCount: 0,
    scheduledCount: 0,
    publishedCount: 0,
    contentTypeBreakdown: {},
  }

  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: "📊" },
    { id: "brands", label: "Brands", icon: "🏢" },
    { id: "creators", label: "Creators", icon: "👥" },
    { id: "approval", label: "Approval", icon: "✅" },
    { id: "ideas", label: "Ideas", icon: "💡" },
    { id: "templates", label: "Templates", icon: "📄" },
    { id: "content", label: "Content", icon: "✍️" },
    { id: "scheduled", label: "Scheduled", icon: "📅" },
    { id: "analytics", label: "Analytics", icon: "📈" },
  ]

  const renderTabContent = () => {
    switch (activeTab) {
      case "dashboard":
        return <DashboardOverview brands={brands} stats={contentStats} onBrandSelect={handleBrandSelect} />
      case "brands":
        return <BrandManagement onBrandSelect={handleBrandSelect} />
      case "creators":
        return <CreatorManagement />
      case "approval":
        return <ContentApproval />
      case "ideas":
        return <IdeaManagement selectedBrand={selectedBrand} />
      case "templates":
        return <TemplateManagement />
      case "content":
        return <ContentCreation selectedBrand={selectedBrand} />
      case "scheduled":
        return <ScheduledPosts selectedBrand={selectedBrand} />
      case "analytics":
        return <Analytics selectedBrand={selectedBrand} />
      default:
        return <DashboardOverview brands={brands} stats={contentStats} onBrandSelect={handleBrandSelect} />
    }
  }

  return (
    <div className="min-h-screen bg-foregroundx">
      {/* Header */}
      <div className="bg-background shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold ">Content Management</h1>
              <p className="">Create, schedule, and manage your social media content</p>
            </div>
            {selectedBrand && brands && (
              <div className="text-sm text-gray-500">
                Active Brand:{" "}
                <span className="font-medium ">
                  {brands.find((b) => b.brand.id === selectedBrand)?.brand.name || "All Brands"}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-background shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id as TabType)}
                className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? "border-blue-500 "
                    : "border-transparent text-gray-500 hover:text-primary hover:border-gray-300"
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{renderTabContent()}</div>
    </div>
  )
}

// Dashboard Overview Component
const DashboardOverview: React.FC<{
  brands: any
  stats: any
  onBrandSelect: (brandId: string | null) => void
}> = ({ brands, stats, onBrandSelect }) => {
  // const { data: recentContent } = trpc.contentManagement.listContent.useQuery({
  //   limit: 5,
  // });

  // const { data: pendingApproval } = trpc.contentManagement.listScrapedContent.useQuery({
  //   status: 'pending',
  //   limit: 3,
  // });
  const recentContent = []
  const pendingApproval = []

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-background p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
                <span className=" font-semibold">📝</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Content</p>
              <p className="text-2xl font-semibold ">{stats?.totalContent || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-background p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-yellow-100 rounded-md flex items-center justify-center">
                <span className="text-yellow-600 font-semibold">📄</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Drafts</p>
              <p className="text-2xl font-semibold ">{stats?.draftCount || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-background p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-100 rounded-md flex items-center justify-center">
                <span className="text-purple-600 font-semibold">📅</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Scheduled</p>
              <p className="text-2xl font-semibold ">{stats?.scheduledCount || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-background p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-100 rounded-md flex items-center justify-center">
                <span className="text-green-600 font-semibold">✅</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Published</p>
              <p className="text-2xl font-semibold ">{stats?.publishedCount || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Brand Selector */}
      <div className="bg-background p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium  mb-4">Your Brands</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <button onClick={() => onBrandSelect(null)} className="p-4 border rounded-lg  text-left transition-colors">
            <div className="font-medium ">All Brands</div>
            <div className="text-sm text-gray-500">View content from all brands</div>
          </button>
          {brands?.map((brand: any) => (
            <button
              key={brand.brand.id}
              onClick={() => onBrandSelect(brand.brand.id)}
              className="p-4 border rounded-lg  text-left transition-colors"
            >
              <div className="font-medium ">{brand.brand.name}</div>
              <div className="text-sm text-gray-500">
                {brand.brand.type} • {brand.contentCount} posts
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Content */}
        <div className="bg-background p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium  mb-4">Recent Content</h3>
          <div className="space-y-4">
            {recentContent?.slice(0, 5).map((item: any) => (
              <div key={item.content.id} className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div
                    className={`w-2 h-2 rounded-full mt-2 ${
                      item.content.status === "published"
                        ? "bg-green-500"
                        : item.content.status === "scheduled"
                          ? "bg-purple-500"
                          : "bg-gray-400"
                    }`}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium  truncate">{item.content.title || "Untitled"}</p>
                  <p className="text-sm text-gray-500">
                    {item.brand.name} • {item.content.status}
                  </p>
                </div>
              </div>
            ))}
            {(!recentContent || recentContent.length === 0) && <p className="text-gray-500 text-sm">No content yet</p>}
          </div>
        </div>

        {/* Pending Approval */}
        <div className="bg-background p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium  mb-4">Pending Approval</h3>
          <div className="space-y-4">
            {pendingApproval?.slice(0, 3).map((item: any) => (
              <div key={item.scraped.id} className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-2 h-2 bg-orange-500 rounded-full mt-2" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium  truncate">{item.creator.name}</p>
                  <p className="text-sm text-gray-500 truncate">{item.scraped.content.substring(0, 80)}...</p>
                  <p className="text-xs text-gray-400">
                    {item.scraped.platform} • {new Date(item.scraped.scrapedAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
            {(!pendingApproval || pendingApproval.length === 0) && (
              <p className="text-gray-500 text-sm">No pending approvals</p>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-background p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium  mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button className="p-4 border border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors text-center">
            <div className="text-2xl mb-2">➕</div>
            <div className="text-sm font-medium ">New Brand</div>
          </button>
          <button className="p-4 border border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors text-center">
            <div className="text-2xl mb-2">👥</div>
            <div className="text-sm font-medium ">Add Creator</div>
          </button>
          <button className="p-4 border border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors text-center">
            <div className="text-2xl mb-2">✍️</div>
            <div className="text-sm font-medium ">Create Content</div>
          </button>
          <button className="p-4 border border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors text-center">
            <div className="text-2xl mb-2">🔄</div>
            <div className="text-sm font-medium ">Trigger Scraping</div>
          </button>
        </div>
      </div>
    </div>
  )
}

export default ContentManagementScreen
